import { PrinterApprovalStatus } from '@core/enums/project';
import { Api, States } from '@core/types';
import useConstructor from '@hooks/useConstructor';
import { projectActionCreators } from '@redux/projects';
import { usePassportContext } from '@tti/passport';
import React, { Fragment, FunctionComponent, useState } from 'react';
import { connect } from 'react-redux';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import CreateProject from './CreateProject';
import PDFRow from './PDFRow';
import ProjectRow from './ProjectRow';

enum Tab {
  PreviousProjects = 0,
  ApprovedPDFs = 1,
  RejectedPDFs = 2,
}

interface IProps {
  project: States.IProjectState;
  fetchProjects: (params: Api.IFetchProjectsRequest) => void;
  fetchUserPdfs: (params: Api.IFetchUserPdfsRequest) => void;
  deleteProjects: (params: Api.IDeleteProjectsRequest) => void;
}

const ProjectList: FunctionComponent<IProps> = ({ project, fetchProjects, fetchUserPdfs, deleteProjects }) => {
  const [showCreateProject, setShowCreateProject] = useState(false);
  const { passportContext } = usePassportContext();
  const [tab, setTab] = useState<number>(Tab.PreviousProjects);
  const [selectedProjects, setSelectedProjects] = useState<string[]>([]);

  useConstructor(() => fetchProjects({ bearerToken: passportContext.bearerToken || '' }));
  useConstructor(() => fetchUserPdfs({ bearerToken: passportContext.bearerToken || '', status: PrinterApprovalStatus.Approved }));
  useConstructor(() => fetchUserPdfs({ bearerToken: passportContext.bearerToken || '', status: PrinterApprovalStatus.Rejected }));

  const handleProjectSelect = (projectGUID: string, isSelected: boolean) => {
    if (isSelected) {
      setSelectedProjects((prev) => [...prev, projectGUID]);
    } else {
      setSelectedProjects((prev) => prev.filter((id) => id !== projectGUID));
    }
  };

  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!project.projects) return;

    const allProjectIds = project.projects.map((p) => p.projectGUID);

    if (e.target.checked) {
      setSelectedProjects(allProjectIds);
    } else {
      setSelectedProjects([]);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedProjects.length === Tab.PreviousProjects) return;

    await deleteProjects({
      bearerToken: passportContext.bearerToken || '',
      projectGUIDs: selectedProjects,
    });

    setSelectedProjects([]);
  };

  const handleTabChange = (newTab: number) => {
    setTab(newTab);
    setSelectedProjects([]);
  };

  if (!project) {
    return null;
  }

  const renderPlaceholderContent = () => {
    const previousProjects = project.projects.length > Tab.PreviousProjects;
    const approvedPdfs = project.approvedPDFs.pdfs.length > Tab.PreviousProjects;
    const rejectedPdfs = project.rejectedPDFs.pdfs.length > Tab.PreviousProjects;

    if (
      (tab === Tab.PreviousProjects && previousProjects) ||
      (tab === Tab.ApprovedPDFs && approvedPdfs) ||
      (tab === Tab.RejectedPDFs && rejectedPdfs)
    ) {
      return;
    }

    let title = 'You have no projects';
    let text = 'Click the button below to start a new project. Any previous projects will appear in this list moving forward.';

    if (tab === Tab.ApprovedPDFs && !approvedPdfs) {
      title = 'You have no approved pdfs';
      text = 'Any approved PDFs will appear in this list moving forward.';
    }

    if (tab === Tab.RejectedPDFs && !rejectedPdfs) {
      title = 'You have no rejected pdfs';
      text = 'Any rejected PDFs will appear in this list moving forward.';
    }

    return (
      <div className="no-projects">
        <h2 className="no-projects__title">{title}</h2>
        <div className="no-projects__text">{text}</div>
        {tab === Tab.PreviousProjects && !previousProjects && (
          <div className="tabs__create">
            <button className="btn btn--inline" onClick={() => setShowCreateProject(true)}>
              Create New Project
            </button>
          </div>
        )}
      </div>
    );
  };

  return (
    <Fragment>
      <div className="tabs">
        <div className="tabs__container">
          <button
            className={`tab ${tab === Tab.PreviousProjects ? 'active' : ''}`}
            onClick={() => handleTabChange(Tab.PreviousProjects)}
          >
            Previous Projects
          </button>
          <button className={`tab ${tab === Tab.ApprovedPDFs ? 'active' : ''}`} onClick={() => handleTabChange(Tab.ApprovedPDFs)}>
            Approved PDFs
          </button>
          <button className={`tab ${tab === Tab.RejectedPDFs ? 'active' : ''}`} onClick={() => handleTabChange(Tab.RejectedPDFs)}>
            Rejected PDFs
          </button>
        </div>
        {project.projects.length > Tab.PreviousProjects && (
          <div className="tabs__create">
            <button className="btn btn--inline" onClick={() => setShowCreateProject(true)}>
              Create New Project
            </button>
          </div>
        )}
      </div>
      <CreateProject visible={showCreateProject} setIsVisible={setShowCreateProject} />
      {tab === Tab.PreviousProjects && project.projects.length > Tab.PreviousProjects && (
        <div className="selected-products__controls">
          <div className="selected-products__select-all">
            <input
              id="selectedAllchkbox1"
              type="checkbox"
              className="selected-products__select-all--input"
              checked={selectedProjects.length === project.projects.length && project.projects.length > Tab.PreviousProjects}
              onChange={handleSelectAll}
            />
            <label htmlFor="selectedAllchkbox1" className="selected-products__select-all--label">
              Select All
            </label>
          </div>
          {selectedProjects.length > Tab.PreviousProjects && (
            <button className="selected-products__remove" onClick={handleBulkDelete}>
              Delete Selected ({selectedProjects.length})
            </button>
          )}
        </div>
      )}
      <div className="projects">
        {renderPlaceholderContent()}
        {tab === Tab.PreviousProjects && project.projects.length > Tab.PreviousProjects && (
          <TransitionGroup>
            {project.projects.map((x) => (
              <CSSTransition key={x.projectGUID} classNames="fade-in" timeout={300}>
                <ProjectRow
                  key={x.projectGUID}
                  project={x}
                  isSelected={selectedProjects.includes(x.projectGUID)}
                  onSelect={handleProjectSelect}
                />
              </CSSTransition>
            ))}
          </TransitionGroup>
        )}
        {tab === Tab.ApprovedPDFs && (
          <TransitionGroup>
            {project.approvedPDFs.pdfs.map((x) => {
              let description = '';

              if (x.printerApprovalStatusUpdatedByUser !== null) {
                description = `Approved By: ${x.printerApprovalStatusUpdatedByUser.userFirstName} ${x.printerApprovalStatusUpdatedByUser.userLastName}`;
              }

              return (
                <CSSTransition key={x.pdfUrl} classNames="fade-in" timeout={300}>
                  <PDFRow
                    key={x.pdfUrl}
                    title={x.project.projectName}
                    url={x.pdfUrl}
                    date={`Created ${x.createdAtISO}`}
                    description={description}
                  />
                </CSSTransition>
              );
            })}
          </TransitionGroup>
        )}
        {tab === Tab.RejectedPDFs && (
          <TransitionGroup>
            {project.rejectedPDFs.pdfs.map((x) => {
              let description = '';

              if (x.printerApprovalStatusUpdatedByUser !== null) {
                description = `Rejected By: ${x.printerApprovalStatusUpdatedByUser.userFirstName} ${x.printerApprovalStatusUpdatedByUser.userLastName}`;
              }

              return (
                <CSSTransition key={x.pdfUrl} classNames="fade-in" timeout={300}>
                  <PDFRow
                    key={x.pdfUrl}
                    title={x.project.projectName}
                    url={x.pdfUrl}
                    date={`Created ${x.createdAtISO}`}
                    description={description}
                  />
                </CSSTransition>
              );
            })}
          </TransitionGroup>
        )}
      </div>
    </Fragment>
  );
};

const mapStateToProps = (state: States.IRootState) => ({
  project: state.project,
});

const mapDispatchToProps = {
  fetchProjects: (params: Api.IBaseAuthorisedRequest) => projectActionCreators.fetchProjects(params),
  fetchUserPdfs: (params: Api.IFetchUserPdfsRequest) => projectActionCreators.fetchUserPdfs(params),
  deleteProjects: (params: Api.IDeleteProjectsRequest) => projectActionCreators.deleteProjects(params),
};

export default connect(mapStateToProps, mapDispatchToProps)(ProjectList);
